import {DemoControl, Outcome, Scenario} from "../model";

/** One control-and-scope, and the only thing anything downstream can read from it. */
export type NodeScope = {scope: string; outcome?: Outcome; supportsReliance: boolean};

export type ControlNode = {
  control_number: string;
  title: string;
  /** The engagement-level assessment the control lives in — the grouping concept on these views. */
  assessment: string;
  /** True where the application performs the control itself. */
  automated: boolean;
  scopes: NodeScope[];
};

export type Edge = {from: string; to: string};

export type LayeredGraph<T extends {control_number: string} = ControlNode> = {
  layers: T[][];
  edges: Edge[];
  /** Stable position maps for the chain helper and column ordering. */
  nodeIndex: Map<string, {layer: number; pos: number}>;
};

export const edgeKey = (e: Edge): string => `${e.from}->${e.to}`;

/** Whether every scope of a control concluded in a way that supports reliance. */
export const nodeSupportsReliance = (node: ControlNode): boolean =>
  node.scopes.length > 0 && node.scopes.every(s => s.supportsReliance);

const makeNode = (control: DemoControl): ControlNode => ({
  control_number: control.ref,
  title: control.title,
  assessment: control.assessment,
  automated: control.automated ?? false,
  scopes: control.scopes.map(s => ({
    scope: s.scope,
    outcome: s.oe?.outcome,
    supportsReliance: s.oe?.supportsReliance ?? false,
  })),
});

/**
 * Layer and order an already-built node set. The demo feeds this from a scenario
 * ([`buildLayeredGraph`]); the live map feeds it from the OE dependency API. Edges
 * are deduped and nodes keyed by `control_number`, so both sources land here.
 */
export function layerGraph<T extends {control_number: string}>(nodeList: T[], rawEdges: Edge[]): LayeredGraph<T> {
  const nodes = new Map<string, T>();
  for (const node of nodeList) nodes.set(node.control_number, node);

  const edges: Edge[] = [];
  const seen = new Set<string>();
  for (const edge of rawEdges) {
    const key = `${edge.from}->${edge.to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push({from: edge.from, to: edge.to});
  }

  const layers = assignLayers(nodes, edges);
  orderColumnsBarycentric(layers, edges);

  const nodeIndex = new Map<string, {layer: number; pos: number}>();
  layers.forEach((layer, layerIdx) => {
    layer.forEach((node, pos) => nodeIndex.set(node.control_number, {layer: layerIdx, pos}));
  });

  return {layers, edges, nodeIndex};
}

export function buildLayeredGraph(scenario: Scenario): LayeredGraph {
  return layerGraph(scenario.controls.map(makeNode), scenario.edges);
}

// layer(n) = 0 if n has no upstream, else max(layer(p) + 1 for each upstream p).
// Works for any DAG; falls back to a sane bound if the data accidentally contains a cycle.
function assignLayers<T extends {control_number: string}>(nodes: Map<string, T>, edges: Edge[]): T[][] {
  const upstreamOf = new Map<string, string[]>();
  for (const e of edges) {
    const list = upstreamOf.get(e.to);
    if (list) list.push(e.from);
    else upstreamOf.set(e.to, [e.from]);
  }

  const layerOf = new Map<string, number>();
  const cap = nodes.size + 1; // cycle bailout
  const compute = (id: string, depth: number): number => {
    if (layerOf.has(id)) return layerOf.get(id)!;
    if (depth > cap) return 0;
    const ups = upstreamOf.get(id) ?? [];
    const layer = ups.length === 0 ? 0 : Math.max(...ups.map(p => compute(p, depth + 1) + 1));
    layerOf.set(id, layer);
    return layer;
  };

  for (const id of nodes.keys()) compute(id, 0);

  const maxLayer = Math.max(0, ...layerOf.values());
  const layers: T[][] = Array.from({length: maxLayer + 1}, () => []);
  // Preserve insertion order for stability — the barycentric pass below reorders.
  for (const [id, node] of nodes) {
    const idx = layerOf.get(id) ?? 0;
    layers[idx].push(node);
  }
  return layers;
}

// One barycentric pass left-to-right: each node's vertical position becomes the mean position of its
// upstream parents, then sort by that. Reduces edge crossings without the full Sugiyama loop.
function orderColumnsBarycentric<T extends {control_number: string}>(layers: T[][], edges: Edge[]) {
  if (layers.length < 2) return;
  const upstreamOf = new Map<string, string[]>();
  for (const e of edges) {
    const list = upstreamOf.get(e.to);
    if (list) list.push(e.from);
    else upstreamOf.set(e.to, [e.from]);
  }

  for (let i = 1; i < layers.length; i++) {
    const prevPos = new Map<string, number>();
    layers[i - 1].forEach((n, pos) => prevPos.set(n.control_number, pos));

    const layer = layers[i];
    const decorated = layer.map((node, originalPos) => {
      const ups = upstreamOf.get(node.control_number) ?? [];
      const positions = ups.map(p => prevPos.get(p)).filter((p): p is number => p !== undefined);
      const barycenter = positions.length === 0 ? originalPos : positions.reduce((a, b) => a + b, 0) / positions.length;
      return {node, originalPos, barycenter};
    });
    decorated.sort((a, b) => a.barycenter - b.barycenter || a.originalPos - b.originalPos);
    layers[i] = decorated.map(d => d.node);
  }
}
